import { castDraft, immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { ElementPermission } from '../../../core/domain/permission-types';
import { ColumnConfigItemEntity } from '../../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../../domain/column-model-item.entity';
import { FilterModelItemEntity } from '../../domain/filter-model-item.entity';
import { InternalMetadataEntity } from '../../domain/internal-metadata.entity';
import { ProfileItemEntity } from '../../domain/profile-item.entity';
import { PublicMetadataEntity } from '../../domain/public-metadata.entity';
import {
	DatablockColumn,
	SubscriptionAction,
} from '../../domain/subscription.types';
import { ConsumedProjectItemEntity } from './consumed-project-item.entity';
import { HistoryItemEntity } from './history-list-modal/history-item.entity';
import { MemberAccessEntity } from './permissions-config-modal/member-access.entity';
import { PublishedSubscriptionEntity } from './published-subscription.entity';

interface IViewDetailsEntity {
	id: number;
	internalMetadata: InternalMetadataEntity;
	publicMetadata: PublicMetadataEntity;
	actionInProgress: SubscriptionAction;
	permissions: Set<ElementPermission>;
	published: PublishedSubscriptionEntity;
	history: Map<string, HistoryItemEntity>;
	membersAccess: Map<string, MemberAccessEntity>;
	availableColumns: Map<string, ColumnConfigItemEntity>;
	profiles: Map<string, ProfileItemEntity>;
	columnModels: Map<string, ColumnModelItemEntity>;
	filterModels: Map<string, FilterModelItemEntity>;
	consumedProjects: Map<string, ConsumedProjectItemEntity>;
}

const entityDefaults: IViewDetailsEntity = {
	id: -1,
	internalMetadata: InternalMetadataEntity.build(),
	publicMetadata: PublicMetadataEntity.build(),
	actionInProgress: 'none',
	permissions: Set(),
	published: PublishedSubscriptionEntity.build(),
	history: Map(),
	membersAccess: Map(),
	availableColumns: Map(),
	profiles: Map(),
	columnModels: Map(),
	filterModels: Map(),
	consumedProjects: Map(),
};

export class ViewDetailsEntity implements IViewDetailsEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public published = entityDefaults.published;
	public history = entityDefaults.history;
	public membersAccess = entityDefaults.membersAccess;
	public internalMetadata = entityDefaults.internalMetadata;
	public publicMetadata = entityDefaults.publicMetadata;
	public permissions = entityDefaults.permissions;
	public availableColumns = entityDefaults.availableColumns;
	public profiles = entityDefaults.profiles;
	public columnModels = entityDefaults.columnModels;
	public filterModels = entityDefaults.filterModels;
	public consumedProjects = entityDefaults.consumedProjects;
	public actionInProgress = entityDefaults.actionInProgress;

	public static build(
		params: Partial<IViewDetailsEntity> = {}
	): ViewDetailsEntity {
		const instance = new ViewDetailsEntity();
		instance.id = params.id ?? entityDefaults.id;
		instance.published = params.published ?? entityDefaults.published;
		instance.history = params.history ?? entityDefaults.history;
		instance.membersAccess =
			params.membersAccess ?? entityDefaults.membersAccess;
		instance.internalMetadata =
			params.internalMetadata ?? entityDefaults.internalMetadata;
		instance.publicMetadata =
			params.publicMetadata ?? entityDefaults.publicMetadata;
		instance.permissions = params.permissions ?? entityDefaults.permissions;
		instance.availableColumns =
			params.availableColumns ?? entityDefaults.availableColumns;
		instance.profiles = params.profiles ?? entityDefaults.profiles;
		instance.columnModels = params.columnModels ?? entityDefaults.columnModels;
		instance.filterModels = params.filterModels ?? entityDefaults.filterModels;
		instance.consumedProjects =
			params.consumedProjects ?? entityDefaults.consumedProjects;
		instance.actionInProgress =
			params.actionInProgress ?? entityDefaults.actionInProgress;

		return instance;
	}

	public setInternalMetadata(
		internalMetadata: InternalMetadataEntity
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.internalMetadata = castDraft(internalMetadata);
		});
	}

	public setPublicMetadata(
		publicMetadata: PublicMetadataEntity
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.publicMetadata = castDraft(publicMetadata);
		});
	}

	public setHistory(
		history: Map<string, HistoryItemEntity>
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.history = castDraft(history);
		});
	}

	public setMembersAccess(
		access: Map<string, MemberAccessEntity>
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.membersAccess = castDraft(access);
		});
	}

	public setProfiles(
		profiles: Map<string, ProfileItemEntity>
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.profiles = castDraft(profiles);
		});
	}

	public setColumnModels(
		columnModels: Map<string, ColumnModelItemEntity>
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.columnModels = castDraft(columnModels);
		});
	}

	public setFilterModels(
		filterModels: Map<string, FilterModelItemEntity>
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.filterModels = castDraft(filterModels);
		});
	}

	public setAvailableColumns(
		columns: Map<string, ColumnConfigItemEntity>
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.availableColumns = castDraft(columns);
		});
	}

	public setConsumedProjects(
		projects: Map<string, ConsumedProjectItemEntity>
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.consumedProjects = castDraft(projects);
		});
	}

	public updateConsumedProjectListWithColumns(
		consumerId: number,
		columns: Array<DatablockColumn>
	): ViewDetailsEntity {
		const selectedConsumedProject = this.consumedProjects.get(`${consumerId}`);
		if (!selectedConsumedProject) {
			return this;
		}
		const updatedItem = selectedConsumedProject.setProfileColumns(columns);
		const updatedList = this.consumedProjects.set(`${consumerId}`, updatedItem);

		return produce(this, (draft) => {
			draft.consumedProjects = castDraft(updatedList);
		});
	}

	public setActionInProgress(action: SubscriptionAction): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.actionInProgress = castDraft(action);
		});
	}

	public setPublished(
		published: PublishedSubscriptionEntity
	): ViewDetailsEntity {
		return produce(this, (draft) => {
			draft.published = castDraft(published);
		});
	}
}
